/**
 * Time formatting utilities for exoplanet transit display.
 */

/**
 * Format a date string to ISO-like UTC display.
 * @param {string|Date} dateString - Date to format
 * @param {boolean} includeUTC - Whether to append " UTC"
 * @returns {string}
 */
export const formatTime = (dateString, includeUTC = true) => {
  const date = new Date(dateString);
  const formatted = date.toISOString().substring(0, 19).replace('T', ' ');
  return includeUTC ? formatted + ' UTC' : formatted;
};

/**
 * Format a duration in hours to "Xh Ym" string.
 * @param {number} hours
 * @returns {string}
 */
export const formatDuration = (hours) => {
  if (!hours) return 'N/A';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

/**
 * Convert a UTC time to an observatory's local timezone.
 * @param {string|Date} utcTime
 * @param {Object} observatory - Observatory with location.timezone
 * @returns {{ local: string|null, utc: string }}
 */
export const getLocalTime = (utcTime, observatory) => {
  if (!observatory?.location?.timezone) {
    return { local: null, utc: formatTime(utcTime, false) };
  }

  try {
    const localOptions = {
      timeZone: observatory.location.timezone,
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      year: 'numeric'
    };
    const localTime = new Date(utcTime).toLocaleString('en-US', localOptions);

    const timeZoneOptions = {
      timeZone: observatory.location.timezone,
      timeZoneName: 'short'
    };
    const tzString = new Date(utcTime).toLocaleString('en-US', timeZoneOptions);
    const tzAbbr = tzString.split(' ').pop();

    const utcStr = formatTime(utcTime, false);

    return {
      local: `${localTime} ${tzAbbr}`,
      utc: utcStr
    };
  } catch (error) {
    console.error('Error converting to local time:', error);
    return {
      local: null,
      utc: formatTime(utcTime, false)
    };
  }
};

/**
 * Format a date string in a specific timezone with full date/time display.
 * @param {string|Date} dateString
 * @param {string} timezone - IANA timezone string
 * @returns {string}
 */
export const formatLocalTime = (dateString, timezone = 'UTC') => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    });
  } catch (error) {
    console.error('Error formatting local time:', error);
    return 'N/A';
  }
};
